// src/components/work_orders/WorkOrderCreateGuide.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — AI assistant for creating a work order or an inspection
//
//   • Live, explainable suggestion while you type (keyword rules from workOrderCatalog):
//       "Looks like Line Striping · Road · Preventive · Medium — because 'striping'"
//   • "Ask FlowSense AI" → POST /workorders/ai/draft: rules + the ML triage model +
//       the linked asset's failure prediction + open-job duplicate check
//   • Step-by-step "how to create this work order" checklist with why each step matters
//   • What the AI does after you press Dispatch (dispatcher, parts, SLA, dig-once …)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, HStack, VStack, Text, Icon, Badge, Button, Progress, Tooltip, Collapse,
    useColorModeValue, Spinner, Alert, AlertIcon, Wrap, WrapItem, Tag,
} from '@chakra-ui/react';
import {
    MdAutoAwesome, MdCheckCircle, MdErrorOutline, MdWarning, MdExpandMore, MdExpandLess,
    MdContentCopy, MdBolt,
} from 'react-icons/md';
import {
    validateDraft, suggestFromText, getDomain, findCategory, descriptionTemplate,
    WORK_ORDER_TYPES, WORK_ORDER_DOMAINS,
} from './workOrderCatalog';
import { getWorkOrderAiDraft } from '../../services/workOrderService';

const SUGGEST_DEBOUNCE_MS = 400;

const AFTER_CREATE = [
    { id: 'sla', text: 'The backend starts the SLA clock from the priority (response + resolution deadlines).' },
    { id: 'dispatch', text: 'The AI dispatcher suggests the best technician (skills, distance, workload). At high confidence it auto-dispatches.' },
    { id: 'parts', text: 'The parts AI reads the description and pre-selects spares from inventory.' },
    { id: 'link', text: 'The job is added to the linked record\'s history, so the prediction models learn from it.' },
    { id: 'digonce', text: 'Road Paving (and uncategorised corrective / emergency) jobs with GPS run the 4D dig-once check — the job is put on hold if utility lines or permits conflict.' },
    { id: 'watchdog', text: 'The SLA watchdog monitors it and escalates if it stalls or nobody accepts it.' },
];

const StepIcon = ({ status }) => (
    <Icon
        as={status === 'ok' ? MdCheckCircle : status === 'warn' ? MdWarning : MdErrorOutline}
        color={status === 'ok' ? 'green.400' : status === 'warn' ? 'orange.400' : 'gray.400'}
        boxSize={4} mt="2px" flexShrink={0}
    />
);
StepIcon.propTypes = { status: PropTypes.string };

function WorkOrderCreateGuide({ draft, onApply, compact }) {
    const panelBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.12)');
    const panelBorder = useColorModeValue('purple.200', 'purple.700');
    const cardBg = useColorModeValue('white', 'gray.800');
    const muted = useColorModeValue('gray.600', 'gray.400');
    const headColor = useColorModeValue('purple.700', 'purple.200');
    const dupBg = useColorModeValue('orange.50', 'rgba(221, 107, 32, 0.15)');

    const [showSteps, setShowSteps] = useState(!compact);
    const [showAfter, setShowAfter] = useState(false);
    const [liveSuggestion, setLiveSuggestion] = useState(null);
    const [ai, setAi] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [isAsking, setIsAsking] = useState(false);
    const isMounted = useRef(true);
    const reqRef = useRef(0);
    useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

    const guide = useMemo(() => validateDraft(draft), [draft]);

    // Live keyword suggestion (debounced) from title + description
    const text = `${draft.title || ''} ${draft.description || ''}`.trim();
    useEffect(() => {
        if (text.length < 6) { setLiveSuggestion(null); return undefined; }
        const id = setTimeout(() => setLiveSuggestion(suggestFromText(text, {})), SUGGEST_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [text]);

    // Only offer what would actually change the form
    const livePatch = useMemo(() => {
        if (!liveSuggestion) return null;
        const p = {};
        if (liveSuggestion.domain && liveSuggestion.domain !== draft.domain) p.domain = liveSuggestion.domain;
        if (liveSuggestion.type && liveSuggestion.type !== draft.work_order_type) p.work_order_type = liveSuggestion.type;
        if (liveSuggestion.category && liveSuggestion.category !== draft.category) p.category = liveSuggestion.category;
        if (liveSuggestion.priority && liveSuggestion.priority !== draft.priority) p.priority = liveSuggestion.priority;
        return Object.keys(p).length ? p : null;
    }, [liveSuggestion, draft.domain, draft.work_order_type, draft.category, draft.priority]);

    const askAi = useCallback(async () => {
        const rid = ++reqRef.current;
        setIsAsking(true);
        setAiError(null);
        try {
            const res = await getWorkOrderAiDraft(draft);
            if (!isMounted.current || rid !== reqRef.current) return;
            setAi(res);
        } catch (err) {
            if (!isMounted.current || rid !== reqRef.current) return;
            setAiError(err.message || 'AI assistant unavailable');
            setAi(null);
        } finally {
            if (isMounted.current && rid === reqRef.current) setIsAsking(false);
        }
    }, [draft]);

    const aiPatch = useMemo(() => {
        const s = ai?.suggestion;
        if (!s) return null;
        const p = {};
        ['domain', 'work_order_type', 'category', 'priority', 'due_date'].forEach((k) => { if (s[k] && s[k] !== draft[k]) p[k] = s[k]; });
        if (s.title && !String(draft.title || '').trim()) p.title = s.title;
        return Object.keys(p).length ? p : null;
    }, [ai, draft]);

    const domain = getDomain(draft.domain);
    const cat = findCategory(draft.category, draft.domain);
    const descEmpty = String(draft.description || '').trim().length < 15;
    const template = useMemo(() => descriptionTemplate(draft), [draft]);
    // Mirrors the backend: the dig-once check runs for 'Road Paving', 'Emergency Repair', 'Corrective Maintenance'
    const showDigOnce = draft.category === 'Road Paving' || (!draft.category && ['emergency', 'corrective'].includes(draft.work_order_type));
    const after = AFTER_CREATE.filter(a => a.id !== 'digonce' || showDigOnce);
    const label = (p) => [
        p.category,
        p.domain && WORK_ORDER_DOMAINS[p.domain]?.short,
        p.work_order_type && WORK_ORDER_TYPES[p.work_order_type]?.short,
        p.priority,
    ].filter(Boolean).join(' · ');

    return (
        <Box bg={panelBg} borderWidth="1px" borderColor={panelBorder} borderRadius="lg" p={3} role="region" aria-label="AI create assistant">
            <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                <HStack spacing={2}>
                    <Icon as={MdAutoAwesome} color="purple.500" />
                    <Text fontSize="sm" fontWeight="800" color={headColor}>
                        AI assistant — {draft.work_order_type === 'inspection' ? 'creating an inspection' : 'creating a work order'}
                    </Text>
                </HStack>
                <HStack spacing={2}>
                    <Badge colorScheme={guide.ready ? 'green' : guide.score >= 60 ? 'yellow' : 'red'} aria-label={`Ready ${guide.score} percent`}>
                        {guide.ready ? 'Ready' : `${guide.missingRequired.length} to do`} · {guide.score}%
                    </Badge>
                    <Button size="xs" colorScheme="purple" leftIcon={isAsking ? <Spinner size="xs" /> : <Icon as={MdBolt} />}
                        onClick={askAi} isDisabled={isAsking} aria-label="Ask FlowSense AI">
                        Ask FlowSense AI
                    </Button>
                </HStack>
            </HStack>
            <Progress value={guide.score} size="xs" colorScheme={guide.ready ? 'green' : 'purple'} borderRadius="full" mb={3} />

            {/* Live suggestion from the words typed */}
            {livePatch && (
                <HStack bg={cardBg} borderRadius="md" p={2} mb={2} spacing={2} align="flex-start" justify="space-between">
                    <Box fontSize="xs">
                        <Text fontWeight="700">Looks like: {label({ ...draft, ...livePatch })}</Text>
                        <Text color={muted}>
                            Because {liveSuggestion.reasons.slice(0, 2).join('; ')} · {liveSuggestion.confidence}% match ({liveSuggestion.method})
                        </Text>
                    </Box>
                    <Button size="xs" variant="outline" colorScheme="purple" onClick={() => onApply(livePatch)} aria-label="Apply suggestion">Apply</Button>
                </HStack>
            )}

            {/* Result from the backend AI */}
            {aiError && (
                <Alert status="warning" borderRadius="md" fontSize="xs" mb={2} py={1}>
                    <AlertIcon boxSize={3} />{aiError} — the checklist below still works offline.
                </Alert>
            )}
            {ai && (
                <Box bg={cardBg} borderRadius="md" p={2} mb={2} fontSize="xs" role="region" aria-label="AI suggestion">
                    <HStack justify="space-between" align="flex-start" spacing={2}>
                        <Box>
                            <Text fontWeight="700">AI suggests: {label(ai.suggestion)}{ai.suggestion.due_date ? ` · due ${ai.suggestion.due_date}` : ''}</Text>
                            <HStack spacing={1} mt={1} flexWrap="wrap">
                                <Badge colorScheme={ai.ml?.used ? 'purple' : 'gray'} fontSize="2xs">{ai.ml?.used ? `ML triage: ${ai.ml.method}` : 'ML triage offline — rules only'}</Badge>
                                {ai.asset_context?.prediction?.risk_score != null && (
                                    <Badge colorScheme={ai.asset_context.prediction.risk_score >= 70 ? 'red' : 'yellow'} fontSize="2xs">
                                        {ai.asset_context.name || 'Asset'} failure risk {ai.asset_context.prediction.risk_score}/100
                                    </Badge>
                                )}
                                <Badge fontSize="2xs">{ai.confidence}% confidence</Badge>
                            </HStack>
                        </Box>
                        {aiPatch && <Button size="xs" colorScheme="purple" onClick={() => onApply(aiPatch)} aria-label="Apply AI suggestion">Apply</Button>}
                    </HStack>
                    {ai.reasons?.length > 0 && (
                        <VStack align="stretch" spacing={0.5} mt={2} color={muted}>
                            {ai.reasons.slice(0, 5).map((r, i) => <Text key={i}>• {r}</Text>)}
                        </VStack>
                    )}
                    {ai.duplicates?.length > 0 && (
                        <Box bg={dupBg} borderRadius="md" p={2} mt={2}>
                            <Text fontWeight="700" color="orange.500">Possible duplicate — open work already on this record:</Text>
                            {ai.duplicates.map(d => <Text key={d.id}>#{String(d.id).slice(0, 8)} · {d.title} ({String(d.status).replace(/_/g, ' ')})</Text>)}
                        </Box>
                    )}
                </Box>
            )}

            {/* Crew checklist for the chosen category */}
            {(cat || ai?.crew_checklist?.length > 0) && (
                <Box mb={2}>
                    <Text fontSize="xs" fontWeight="700" mb={1}>Crew will need</Text>
                    <Wrap spacing={1}>
                        {(cat?.checklist || ai.crew_checklist).map(c => <WrapItem key={c}><Tag size="sm" variant="subtle" colorScheme="purple">{c}</Tag></WrapItem>)}
                    </Wrap>
                </Box>
            )}
            {descEmpty && template && (
                <Button size="xs" variant="ghost" colorScheme="purple" leftIcon={<Icon as={MdContentCopy} />} mb={2}
                    onClick={() => onApply({ description: `${String(draft.description || '').trim() ? `${draft.description.trim()}\n` : ''}${template}` })}
                    aria-label="Insert description template">
                    Insert description template
                </Button>
            )}

            {/* How to create it — step by step */}
            <Button size="xs" variant="link" colorScheme="purple" rightIcon={<Icon as={showSteps ? MdExpandLess : MdExpandMore} />} onClick={() => setShowSteps(v => !v)}>
                How to create this {draft.work_order_type === 'inspection' ? 'inspection' : 'work order'} ({guide.steps.filter(s => s.status === 'ok').length}/{guide.steps.length})
            </Button>
            <Collapse in={showSteps} animateOpacity>
                <VStack as="ol" align="stretch" spacing={1.5} mt={2} pl={0} style={{ listStyle: 'none' }} aria-label="Steps to create">
                    {guide.steps.map((s, i) => (
                        <HStack as="li" key={s.id} align="flex-start" spacing={2} fontSize="xs">
                            <StepIcon status={s.status} />
                            <Box>
                                <Text fontWeight="700">
                                    {i + 1}. {s.label}{' '}
                                    {s.level === 'required' && s.status !== 'ok' && <Badge colorScheme="red" fontSize="2xs" ml={1}>needed</Badge>}
                                </Text>
                                {s.warning && <Text color="orange.500">{s.warning}</Text>}
                                {s.hint && <Text color={muted}>{s.hint}</Text>}
                                {s.status !== 'ok' && (
                                    <Tooltip label={s.why} hasArrow placement="top-start">
                                        <Text color="purple.500" cursor="help" w="fit-content">Why?</Text>
                                    </Tooltip>
                                )}
                            </Box>
                        </HStack>
                    ))}
                </VStack>
            </Collapse>

            {/* What happens after Dispatch */}
            <Box mt={2}>
                <Button size="xs" variant="link" colorScheme="purple" rightIcon={<Icon as={showAfter ? MdExpandLess : MdExpandMore} />} onClick={() => setShowAfter(v => !v)}>
                    What the AI does after you create it
                </Button>
                <Collapse in={showAfter} animateOpacity>
                    <VStack align="stretch" spacing={1} mt={2} fontSize="xs" color={muted}>
                        {after.map(a => <Text key={a.id}>• {a.text}</Text>)}
                        {domain && <Text>• Linked to {domain.label.toLowerCase()}{domain.linkField && draft[domain.linkField] ? ` ${draft[domain.linkField]}` : ''} — open it from the work order to see its history.</Text>}
                        {draft.work_order_type === 'inspection' && <Text>• Record findings in the resolution notes when you complete it — FlowSense then offers to raise follow-up work orders from them.</Text>}
                    </VStack>
                </Collapse>
            </Box>
        </Box>
    );
}

WorkOrderCreateGuide.propTypes = {
    draft: PropTypes.object.isRequired,
    onApply: PropTypes.func.isRequired,
    compact: PropTypes.bool,
};

export default React.memo(WorkOrderCreateGuide);